import Holidays, { type HolidaysTypes } from "date-holidays";
import { DateTime } from "luxon";
import type { Holiday } from "./holidayApi";

/** A common holiday the Add holiday popup offers, on its next date. */
export type HolidayPreset = { name: string; date: DateTime };

const COUNTRY = "US";
const OFFERED_TYPES = new Set(["public", "optional"]);
/** Observances facilities often staff as holidays, beyond the public and optional ones. Matched by English name. */
const EXTRA_NAMES = new Set(["New Year's Eve", "Easter Sunday", "Day after Thanksgiving Day"]);
const ISO_DATE_LENGTH = "yyyy-MM-dd".length;

const sameName = (a: string, b: string): boolean => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

/** The same holiday in two languages shares its rule and its date. */
const holidayKey = (holiday: HolidaysTypes.Holiday): string => `${holiday.rule}|${holiday.date}`;

/** Each holiday's name in `language`, by `holidayKey`. Empty for English, which the calendar already speaks. */
const localNames = (year: number, language: string): ReadonlyMap<string, string> => {
	if (language.toLowerCase().startsWith("en")) return new Map();
	// A calendar of its own: `getHolidays(year, language)` would add the language to the shared one for good.
	const local = new Holidays(COUNTRY, { languages: [language] });
	return new Map(local.getHolidays(year).map((holiday) => [holidayKey(holiday), holiday.name]));
};

/**
 * Common US holidays, each on its next date from `today` (today included), in date order, named in the
 * user's `language`. A holiday already added for that year — same name in either language, or anything
 * on that date — isn't offered.
 */
export const upcomingHolidayPresets = (today: DateTime, existing: readonly Pick<Holiday, "name" | "date">[], language: string): HolidayPreset[] => {
	const calendar = new Holidays(COUNTRY);
	const nextByName = new Map<string, HolidayPreset & { englishName: string }>();
	for (const year of [today.year, today.year + 1]) {
		const names = localNames(year, language);
		for (const holiday of calendar.getHolidays(year)) {
			const offered = (OFFERED_TYPES.has(holiday.type) && !holiday.substitute) || EXTRA_NAMES.has(holiday.name);
			if (!offered) continue;
			const date = DateTime.fromISO(holiday.date.slice(0, ISO_DATE_LENGTH), { zone: today.zone });
			if (date < today) continue;
			const earlier = nextByName.get(holiday.name);
			if (earlier !== undefined && earlier.date <= date) continue;
			// DEFAULT: a holiday the calendar has no name for in this language keeps its English name.
			const name = names.get(holidayKey(holiday)) ?? holiday.name;
			nextByName.set(holiday.name, { name, englishName: holiday.name, date });
		}
	}
	const taken = (preset: HolidayPreset & { englishName: string }) =>
		existing.some(
			(holiday) =>
				holiday.date.hasSame(preset.date, "day") ||
				(holiday.date.year === preset.date.year && (sameName(holiday.name, preset.name) || sameName(holiday.name, preset.englishName))),
		);
	return [...nextByName.values()]
		.filter((preset) => !taken(preset))
		.sort((a, b) => a.date.toMillis() - b.date.toMillis())
		.map(({ name, date }) => ({ name, date }));
};
